import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import {
    Button,
    CircularProgress,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import BlockIcon from "@material-ui/icons/Block";
import WarningIcon from "@material-ui/icons/Warning";
import PersonIcon from "@material-ui/icons/Person";

import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet } from "../getConfig";

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: 24,
        backgroundColor: "#f8f9fa",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 24,
    },
    headerLeft: {},
    title: {
        fontSize: 24,
        fontWeight: 600,
        color: "#1a1a1a",
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 14,
        color: "#666",
    },
    refreshButton: {
        borderRadius: 6,
        textTransform: "none",
        fontWeight: 500,
    },
    statsRow: {
        display: "flex",
        gap: 16,
        marginBottom: 24,
    },
    statCard: {
        flex: 1,
        padding: 20,
        borderRadius: 8,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        textAlign: "center" as const,
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: "2px solid transparent",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        },
    },
    statCardActive: {
        border: "2px solid #1976d2",
        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)",
    },
    statValue: {
        fontSize: 32,
        fontWeight: 700,
        color: "#1a1a1a",
        lineHeight: 1,
        marginBottom: 4,
    },
    statLabel: {
        fontSize: 12,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
    },
    statFlagged: {
        backgroundColor: "#fff3e0",
        "& $statValue": { color: "#e65100" },
    },
    statRejected: {
        backgroundColor: "#ffebee",
        "& $statValue": { color: "#c62828" },
    },
    statApproved: {
        backgroundColor: "#e8f5e9",
        "& $statValue": { color: "#2e7d32" },
    },
    tablePaper: {
        borderRadius: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        overflow: "hidden",
    },
    tableHeader: {
        padding: "16px 20px",
        borderBottom: "1px solid #eee",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    tableTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: "#1a1a1a",
    },
    table: {
        "& .MuiTableCell-head": {
            fontWeight: 600,
            fontSize: 12,
            color: "#666",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            backgroundColor: "#f8f9fa",
            padding: "12px 16px",
        },
        "& .MuiTableCell-body": {
            fontSize: 13,
            padding: "12px 16px",
        },
    },
    fraudChip: {
        fontSize: 10,
        fontWeight: 600,
        height: 22,
        marginRight: 4,
        marginBottom: 4,
    },
    actionButtons: {
        display: "flex",
        gap: 8,
    },
    approveButton: {
        backgroundColor: "#4caf50",
        color: "#fff",
        fontSize: 12,
        padding: "4px 12px",
        "&:hover": { backgroundColor: "#388e3c" },
    },
    rejectButton: {
        backgroundColor: "#f44336",
        color: "#fff",
        fontSize: 12,
        padding: "4px 12px",
        "&:hover": { backgroundColor: "#d32f2f" },
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        padding: 40,
    },
    noData: {
        textAlign: "center" as const,
        padding: 60,
        color: "#999",
    },
    noDataIcon: {
        fontSize: 48,
        color: "#ddd",
        marginBottom: 16,
    },
    dialogContent: {
        minWidth: 500,
        padding: 24,
    },
    detailRow: {
        display: "flex",
        marginBottom: 12,
    },
    detailLabel: {
        width: 140,
        fontSize: 12,
        color: "#666",
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
    },
    detailValue: {
        flex: 1,
        fontSize: 14,
        color: "#1a1a1a",
    },
    fraudFlags: {
        marginTop: 16,
        padding: 16,
        backgroundColor: "#ffebee",
        borderRadius: 6,
    },
    fraudFlagsTitle: {
        fontSize: 12,
        fontWeight: 600,
        color: "#c62828",
        marginBottom: 8,
        textTransform: "uppercase" as const,
        letterSpacing: 0.5,
    },
    noteField: {
        marginTop: 16,
        "& .MuiOutlinedInput-root": {
            borderRadius: 6,
        },
    },
}));

const FRAUD_REVIEW_ENDPOINT = API + "/admin/referral-v2-fraud-review";

const FRAUD_FLAG_LABELS: Record<string, { label: string; color: string }> = {
    SAME_DEVICE: { label: "Same Device", color: "#c62828" },
    CIRCULAR_REFERRAL: { label: "Circular Referral", color: "#c62828" },
    CHAIN_PATTERN: { label: "Chain Pattern", color: "#e65100" },
    SUSPICIOUS_EMAIL: { label: "Suspicious Email", color: "#e65100" },
};

const ReferralFraudReview = () => {
    const classes = useStyles();
    const notify = useNotify();

    const [loading, setLoading] = useState(true);
    const [fraudLogs, setFraudLogs] = useState<any[]>([]);
    const [stats, setStats] = useState({ flagged: 0, cleared: 0, rejected: 0 });
    const [activeFilter, setActiveFilter] = useState<'flagged' | 'cleared' | 'rejected'>('flagged');

    const [selectedCommission, setSelectedCommission] = useState<any | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [reviewNote, setReviewNote] = useState("");

    const loadData = async (filter: string = activeFilter) => {
        setLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.get(`${FRAUD_REVIEW_ENDPOINT}?filter=${filter}`, config);

            if (response.data?.status) {
                setFraudLogs(response.data.data?.fraudLogs || []);
                setStats(response.data.data?.stats || { flagged: 0, cleared: 0, rejected: 0 });
            }
        } catch (error: any) {
            notify("Failed to load fraud logs", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterClick = (filter: 'flagged' | 'cleared' | 'rejected') => {
        setActiveFilter(filter);
        loadData(filter);
    };

    const handleReview = (fraudLog: any) => {
        setSelectedCommission(fraudLog);
        setReviewNote("");
        setDialogOpen(true);
    };

    const handleAction = async (action: "approve" | "reject") => {
        if (!selectedCommission) return;

        setActionLoading(true);
        try {
            const config = getConfigGet({});
            const response: any = await axios.patch(
                `${FRAUD_REVIEW_ENDPOINT}/${selectedCommission.id}`,
                {
                    action,
                    notes: reviewNote || null,
                },
                config
            );

            if (response.data?.status) {
                notify(`Commission ${action === "approve" ? "approved" : "rejected"} successfully`, "success");
                setDialogOpen(false);
                loadData();
            } else {
                notify(response.data?.message || `Failed to ${action} commission`, "error");
            }
        } catch (error: any) {
            notify(`Failed to ${action} commission`, "error");
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const renderFraudFlags = (flags: any) => {
        if (!flags || (Array.isArray(flags) && flags.length === 0)) return "-";

        const flagList = Array.isArray(flags) ? flags : [flags];
        return flagList.map((flag: any, index: number) => {
            const flagKey = typeof flag === "string" ? flag : flag.type;
            const flagInfo = FRAUD_FLAG_LABELS[flagKey] || { label: flagKey, color: "#666" };
            return (
                <Chip
                    key={index}
                    size="small"
                    label={flagInfo.label}
                    className={classes.fraudChip}
                    style={{ backgroundColor: flagInfo.color, color: "#fff" }}
                />
            );
        });
    };

    return (
        <div className={classes.main}>
            <div className={classes.header}>
                <div className={classes.headerLeft}>
                    <Typography className={classes.title}>Fraud Review Queue</Typography>
                    <Typography className={classes.subtitle}>
                        Review and approve/reject flagged referral commissions
                    </Typography>
                </div>
                <Button
                    className={classes.refreshButton}
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => loadData()}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </div>

            <div className={classes.statsRow}>
                <Box
                    className={`${classes.statCard} ${classes.statFlagged} ${activeFilter === 'flagged' ? classes.statCardActive : ''}`}
                    onClick={() => handleFilterClick('flagged')}
                >
                    <Typography className={classes.statValue}>{stats.flagged}</Typography>
                    <Typography className={classes.statLabel}>Pending Review</Typography>
                </Box>
                <Box
                    className={`${classes.statCard} ${classes.statApproved} ${activeFilter === 'cleared' ? classes.statCardActive : ''}`}
                    onClick={() => handleFilterClick('cleared')}
                >
                    <Typography className={classes.statValue}>{stats.cleared}</Typography>
                    <Typography className={classes.statLabel}>Cleared (30d)</Typography>
                </Box>
                <Box
                    className={`${classes.statCard} ${classes.statRejected} ${activeFilter === 'rejected' ? classes.statCardActive : ''}`}
                    onClick={() => handleFilterClick('rejected')}
                >
                    <Typography className={classes.statValue}>{stats.rejected}</Typography>
                    <Typography className={classes.statLabel}>Rejected (30d)</Typography>
                </Box>
            </div>

            {loading ? (
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
            ) : fraudLogs.length > 0 ? (
                <Paper className={classes.tablePaper}>
                    <div className={classes.tableHeader}>
                        <Typography className={classes.tableTitle}>
                            {activeFilter === 'flagged' ? 'Pending Review' : activeFilter === 'cleared' ? 'Cleared' : 'Rejected'} ({fraudLogs.length})
                        </Typography>
                    </div>
                    <TableContainer>
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Referrer</TableCell>
                                    <TableCell>Referred User</TableCell>
                                    <TableCell>Flag Type</TableCell>
                                    <TableCell>Commission</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Created</TableCell>
                                    {activeFilter === 'flagged' && <TableCell align="center">Actions</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {fraudLogs.map((log) => (
                                    <TableRow key={log.id}>
                                        <TableCell>{log.id}</TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                                <PersonIcon fontSize="small" style={{ color: "#999" }} />
                                                <div>
                                                    <div style={{ fontWeight: 500 }}>
                                                        {log.referrer_name || `#${log.referrer_user_id}`}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "#666" }}>
                                                        {log.referrer_email}
                                                    </div>
                                                </div>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                                <PersonIcon fontSize="small" style={{ color: "#999" }} />
                                                <div>
                                                    <div style={{ fontWeight: 500 }}>
                                                        {log.referred_name || `#${log.referred_user_id}`}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "#666" }}>
                                                        {log.referred_email}
                                                    </div>
                                                </div>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={log.flag_type || 'Unknown'}
                                                className={classes.fraudChip}
                                                style={{
                                                    backgroundColor: FRAUD_FLAG_LABELS[log.flag_type]?.color || '#666',
                                                    color: '#fff'
                                                }}
                                            />
                                            {log.severity && (
                                                <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>
                                                    Severity: {log.severity}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {log.commission_amount ? (
                                                <span style={{ fontWeight: 600 }}>£{Number(log.commission_amount).toFixed(2)}</span>
                                            ) : (
                                                <span style={{ color: "#999" }}>-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={log.action_taken}
                                                style={{
                                                    backgroundColor: log.action_taken === 'flagged' ? '#ff9800' :
                                                        log.action_taken === 'cleared' ? '#4caf50' : '#f44336',
                                                    color: '#fff',
                                                    fontSize: 10
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(log.createdAt)}</TableCell>
                                        {activeFilter === 'flagged' && (
                                            <TableCell align="center">
                                                <div className={classes.actionButtons}>
                                                    <Button
                                                        size="small"
                                                        className={classes.approveButton}
                                                        startIcon={<CheckIcon style={{ fontSize: 14 }} />}
                                                        onClick={() => handleReview(log)}
                                                    >
                                                        Review
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            ) : (
                <Paper className={classes.tablePaper}>
                    <div className={classes.noData}>
                        <CheckIcon className={classes.noDataIcon} />
                        <Typography variant="h6" style={{ marginBottom: 8 }}>
                            {activeFilter === 'flagged' ? 'All Clear' : `No ${activeFilter} items`}
                        </Typography>
                        <Typography>
                            {activeFilter === 'flagged'
                                ? 'No flagged referrals pending review'
                                : `No ${activeFilter} fraud logs found`}
                        </Typography>
                    </div>
                </Paper>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle style={{ padding: "16px 24px", borderBottom: "1px solid #eee" }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography style={{ fontSize: 16, fontWeight: 600 }}>Review Fraud Flag</Typography>
                        <IconButton size="small" onClick={() => setDialogOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    {selectedCommission && (
                        <>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Fraud Log ID</span>
                                <span className={classes.detailValue}>{selectedCommission.id}</span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Referral ID</span>
                                <span className={classes.detailValue}>{selectedCommission.referral_id}</span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Commission</span>
                                <span className={classes.detailValue} style={{ fontWeight: 600 }}>
                                    {selectedCommission.commission_amount
                                        ? `£${Number(selectedCommission.commission_amount).toFixed(2)}`
                                        : 'N/A'}
                                </span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Referrer</span>
                                <span className={classes.detailValue}>
                                    {selectedCommission.referrer_name || `User #${selectedCommission.referrer_user_id}`} ({selectedCommission.referrer_email || 'N/A'})
                                </span>
                            </div>
                            <div className={classes.detailRow}>
                                <span className={classes.detailLabel}>Referred User</span>
                                <span className={classes.detailValue}>
                                    {selectedCommission.referred_name || `User #${selectedCommission.referred_user_id}`} ({selectedCommission.referred_email || 'N/A'})
                                </span>
                            </div>

                            <div className={classes.fraudFlags}>
                                <Typography className={classes.fraudFlagsTitle}>
                                    <WarningIcon style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />
                                    Fraud Flag Details
                                </Typography>
                                <div style={{ marginBottom: 8 }}>
                                    <Chip
                                        size="small"
                                        label={selectedCommission.flag_type || 'Unknown'}
                                        style={{
                                            backgroundColor: FRAUD_FLAG_LABELS[selectedCommission.flag_type]?.color || '#666',
                                            color: '#fff'
                                        }}
                                    />
                                    {selectedCommission.severity && (
                                        <span style={{ marginLeft: 8, fontSize: 12, color: '#666' }}>
                                            Severity: {selectedCommission.severity}
                                        </span>
                                    )}
                                </div>
                                {selectedCommission.details && (
                                    <pre style={{ fontSize: 11, whiteSpace: "pre-wrap", backgroundColor: '#fff', padding: 8, borderRadius: 4 }}>
                                        {typeof selectedCommission.details === 'string'
                                            ? selectedCommission.details
                                            : JSON.stringify(selectedCommission.details, null, 2)}
                                    </pre>
                                )}
                            </div>

                            <TextField
                                className={classes.noteField}
                                label="Review Note (optional)"
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={2}
                                value={reviewNote}
                                onChange={(e) => setReviewNote(e.target.value)}
                                placeholder="Add any notes about this review decision..."
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: "16px 24px", borderTop: "1px solid #eee" }}>
                    <Button
                        onClick={() => handleAction("reject")}
                        disabled={actionLoading}
                        startIcon={<BlockIcon />}
                        style={{ color: "#c62828" }}
                    >
                        Reject
                    </Button>
                    <Button
                        onClick={() => handleAction("approve")}
                        disabled={actionLoading}
                        variant="contained"
                        startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                        style={{ backgroundColor: "#4caf50", color: "#fff" }}
                    >
                        Clear (Approve)
                    </Button>
                </DialogActions>
            </Dialog>

            <Notification />
        </div>
    );
};

export default ReferralFraudReview;
